import React, {
	useMemo,
	type MouseEvent,
	type KeyboardEvent,
	useCallback,
	useState,
	type ReactElement,
} from 'react';
import FocusLock from 'react-focus-lock';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronRight from '@atlaskit/icon/glyph/chevron-right';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { Field } from '../../../../../common/constants/fields';
import type { ColumnState } from '../../../../../common/types';
import { FieldSelectorItem } from '../../../../../common/ui/field-selector-item';
import { SearchBar } from '../../../../../common/ui/search-bar';
import { getIcon } from '../../../../../common/utils/fields';
import { useMappingState } from '../../../../../controllers/import-state';
import messages from './messages';

const isCustomField = (field: Field) => !field.fieldId?.startsWith('customfield');

type MappedField = {
	name: string;
	node: ReactElement;
};

export type Props = {
	fieldMap: ColumnState;
	onCustomFieldMenuOpen: (e: MouseEvent<Element> | KeyboardEvent<Element>) => void;
};

export const ExistingFields = ({ fieldMap, onCustomFieldMenuOpen }: Props) => {
	const [mappingState, { setFieldForColumn }] = useMappingState();
	const { formatMessage } = useIntl();
	const [searchTerm, setSearchTerm] = useState('');

	const onItemClick = useCallback(
		(field: Field | null) => {
			setFieldForColumn(fieldMap.columnName, field);
		},
		[fieldMap.columnName, setFieldForColumn],
	);

	const getJiraFields = useCallback(
		(compare: (filed: Field) => boolean) =>
			mappingState?.availableJiraFields
				?.filter((field) => field.fieldId !== fieldMap.field?.fieldId)
				.filter(compare)
				.map((field: Field) => {
					const isDisabled = !!field.fieldId && mappingState?.mappedJiraFieldIds.has(field.fieldId);
					return {
						name: field.fieldName,
						node: (
							<FieldSelectorItem
								key={`${field.fieldName}-${field.fieldType}`}
								name={field.fieldName}
								icon={getIcon({
									fieldType: field.fieldType,
									isSelected: false,
									isDisabled,
								})}
								onClick={() => onItemClick(field)}
								isDisabled={isDisabled}
								tooltipContent={formatMessage(messages.disabledTooltip)}
								tooltipPosition="bottom-end"
							/>
						),
					};
				}),
		[
			fieldMap.field,
			formatMessage,
			mappingState?.availableJiraFields,
			mappingState?.mappedJiraFieldIds,
			onItemClick,
		],
	);

	const availableLinkFieldTypeItems = useMemo(
		() =>
			mappingState?.availableIssueLinkFields
				?.filter(
					(field) =>
						!(
							field.fieldId === fieldMap.field?.fieldId &&
							field.fieldDirection === fieldMap.field?.fieldDirection
						),
				)
				.map((field: Field) => {
					const isDisabled =
						!!field.fieldId &&
						field.fieldId !== fieldMap.field?.fieldId &&
						mappingState?.mappedJiraFieldIds.has(field.fieldId);
					return {
						name: field.fieldName,
						node: (
							<FieldSelectorItem
								key={`${field.fieldId}-${field.fieldName}`}
								name={field.fieldName}
								icon={getIcon({
									fieldType: field.fieldType,
									isSelected: false,
									isDisabled,
								})}
								onClick={() => onItemClick(field)}
								isDisabled={isDisabled}
								tooltipContent={formatMessage(messages.disabledTooltip)}
							/>
						),
					};
				}) || [],
		[
			fieldMap.field?.fieldDirection,
			fieldMap.field?.fieldId,
			formatMessage,
			mappingState?.availableIssueLinkFields,
			mappingState?.mappedJiraFieldIds,
			onItemClick,
		],
	);

	const availableSystemFieldItems = useMemo(
		() => getJiraFields((field: Field) => isCustomField(field)) || [],
		[getJiraFields],
	);

	const availableCustomFieldItems = useMemo(
		() => getJiraFields((field: Field) => !isCustomField(field)) || [],
		[getJiraFields],
	);

	const getSearchFilteredFields = useCallback(
		(mappedFields: MappedField[] | undefined) =>
			mappedFields
				?.filter((field) => field.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()))
				.map((field) => field.node) || [],
		[searchTerm],
	);

	const selectedCustomFieldItem = useMemo(() => {
		if (fieldMap.field && fieldMap.field.fieldId === null) {
			return (
				<FieldSelectorItem
					name={fieldMap.field.fieldName}
					icon={getIcon({
						fieldType: fieldMap.field.fieldType,
						isSelected: true,
					})}
					isSelected
				/>
			);
		}
		return null;
	}, [fieldMap.field]);

	const selectedFieldItem = useMemo(() => {
		if (
			fieldMap.field &&
			fieldMap.field.fieldId !== null &&
			fieldMap.field.fieldName.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
		) {
			return (
				<FieldSelectorItem
					name={fieldMap.field.fieldName}
					icon={getIcon({
						fieldType: fieldMap.field.fieldType,
						isSelected: true,
					})}
					isSelected
				/>
			);
		}
		return null;
	}, [fieldMap.field, searchTerm]);

	const systemFields = useMemo(
		() => getSearchFilteredFields(availableSystemFieldItems),
		[availableSystemFieldItems, getSearchFilteredFields],
	);
	const customFields = useMemo(
		() => getSearchFilteredFields(availableCustomFieldItems),
		[availableCustomFieldItems, getSearchFilteredFields],
	);
	const issueLinks = useMemo(
		() => getSearchFilteredFields(availableLinkFieldTypeItems),
		[availableLinkFieldTypeItems, getSearchFilteredFields],
	);
	const isEmptyView =
		systemFields.length === 0 &&
		customFields.length === 0 &&
		issueLinks.length === 0 &&
		(!fieldMap.field ||
			!fieldMap.field.fieldName.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()));

	return (
		<FocusLock autoFocus>
			<Box
				testId="project-create-importer-upload-flow.ui.mapping-screen.field-mapping.field-selector.existing-fields.box"
				xcss={dropdownWrapperStyles}
			>
				<Box xcss={searchWrapperStyles}>
					<SearchBar
						placeholder={messages.searchAllFieldsPlaceholder}
						onSearchTermChange={(e) => setSearchTerm(e.target.value)}
					/>
				</Box>
				<Box xcss={scrollStyles}>
					{selectedFieldItem}
					{systemFields.length > 0 && (
						<>
							<Box xcss={headingStyles}>{formatMessage(messages.mapToSystemField)}</Box>
							<DropdownItemGroup>{systemFields}</DropdownItemGroup>
						</>
					)}
					{customFields.length > 0 && (
						<>
							<Box xcss={headingStyles}>{formatMessage(messages.mapToCustomField)}</Box>
							<DropdownItemGroup>{customFields}</DropdownItemGroup>
						</>
					)}
					{issueLinks.length > 0 && (
						<>
							<Box xcss={headingStyles}>{formatMessage(messages.mapToIssueLink)}</Box>
							<DropdownItemGroup>{issueLinks}</DropdownItemGroup>
						</>
					)}
					{isEmptyView && (
						<Stack xcss={emptyViewStyles} alignInline="center" alignBlock="center" grow="fill">
							<Box>{formatMessage(messages.noOptionsFound)}</Box>
						</Stack>
					)}
				</Box>
				<Box role="menu" xcss={fixedStyles}>
					<DropdownItem
						elemAfter={<ChevronRight size="medium" label="" />}
						onClick={onCustomFieldMenuOpen}
						testId="project-create-importer-upload-flow.ui.mapping-screen.field-mapping.field-selector.existing-fields.create-new-custom-field"
					>
						<Box as="span" xcss={newCustomFieldStyle}>
							{formatMessage(messages.createNewCustomField)}
						</Box>
					</DropdownItem>
					{selectedCustomFieldItem}
					{fieldMap.field && (
						<>
							<Box xcss={dividerStyles} />
							<DropdownItem
								onClick={() => onItemClick(null)}
								testId="project-create-importer-upload-flow.ui.mapping-screen.field-mapping.field-selector.existing-fields.clear-selection"
							>
								<Box as="span" xcss={clearSelectionStyle}>
									{formatMessage(messages.clearSelection)}
								</Box>
							</DropdownItem>
						</>
					)}
				</Box>
			</Box>
		</FocusLock>
	);
};

const dropdownWrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	maxHeight: '47vh',
	width: '300px',
	paddingBlockEnd: 'space.100',
});

const dividerStyles = xcss({
	borderTopWidth: '2px',
	borderTopStyle: 'solid',
	borderTopColor: 'color.border',
	marginBlock: 'space.075',
});

const searchWrapperStyles = xcss({
	marginInline: 'space.200',
	marginBlock: 'space.200',
});

const scrollStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	overflowY: 'auto',
	flexGrow: '1',
});

const fixedStyles = xcss({
	flex: '0 1 auto',
	borderTopWidth: '2px',
	borderTopStyle: 'solid',
	borderTopColor: 'color.border',
	paddingBlockStart: 'space.075',
});

const headingStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body.small', fontFallback.body.small),
	fontWeight: token('font.weight.bold', '700'),
	marginInline: 'space.200',
	marginBlockStart: 'space.100',
});

const emptyViewStyles = xcss({
	height: '100px',
	font: token('font.body'),
});

const newCustomFieldStyle = xcss({
	font: token('font.body.UNSAFE_small'),
	color: 'color.text.subtle',
	fontWeight: '700',
});

const clearSelectionStyle = xcss({
	color: 'color.link',
});
